/* Router dependencies */
import Vue from 'vue';
import VueRouter from 'vue-router';
import { colorLog, debug } from '@/helpers/utils.js';

Vue.use(VueRouter);

const guardStep = async (to, from, next) => {
  debug &&
    colorLog({
      label: '[GUARD][guardStep]',
      message: 'Guard check in progress...',
      themeName: 'blue',
    });
  if (to.params.followingView) {
    const indexReached = router.app.$store.state.contact.views.findIndex(
      (e) => e.name === router.app.$store.state.contact.reachedView
    );
    const indexTo = router.app.$store.state.contact.views.findIndex(
      (e) => e.name === to.params.followingView
    );
    if (indexTo !== -1 && indexTo > indexReached) {
      await router.app.$store.dispatch(
        'contact/setReachedView',
        to.params.followingView
      );
    }
  }

  const indexReached = router.app.$store.state.contact.views.findIndex(
    (e) => e.name === router.app.$store.state.contact.reachedView
  );
  const indexTo = router.app.$store.state.contact.views.findIndex(
    (e) => e.name === to.name
  );
  if (indexTo >= 0 && indexTo <= indexReached) {
    debug &&
      colorLog({
        label: '[GUARD][guardStep]',
        message: 'Passed',
        themeName: 'vue',
      });

    next();
  } else {
    if (indexReached <= 0) {
      debug &&
        colorLog({
          label: '[GUARD][guardStep]',
          message: 'Home not passed',
          themeName: 'orange',
        });

      next({ name: 'home' });
    } else {
      debug &&
        colorLog({
          label: '[GUARD][guardStep]',
          message: 'Deny',
          themeName: 'red',
        });

      next(false);
    }
  }
};

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/spa/contact-form/pages/home-page.vue'),
  },
  {
    path: '/auth/',
    name: 'auth',
    component: () => import('@/spa/contact-form/pages/auth-page.vue'),
    beforeEnter: guardStep,
  },
  {
    path: '/how-to-contact-us/',
    name: 'how-to-contact-us',
    component: () =>
      import('@/spa/contact-form/pages/how-to-contact-us-page.vue'),
    beforeEnter: guardStep,
  },
  {
    path: '/what-to-tell-us/',
    name: 'what-to-tell-us',
    component: () =>
      import('@/spa/contact-form/pages/what-to-tell-us-page.vue'),
    beforeEnter: guardStep,
  },
  {
    path: '/your-message/',
    name: 'your-message',
    component: () => import('@/spa/contact-form/pages/your-message-page.vue'),
    beforeEnter: guardStep,
  },
  {
    path: '/your-personal-data/',
    name: 'your-personal-data',
    component: () =>
      import('@/spa/contact-form/pages/your-personal-data-page.vue'),
    beforeEnter: guardStep,
  },
  {
    path: '/your-fiscal-data/',
    name: 'your-fiscal-data',
    component: () =>
      import('@/spa/contact-form/pages/your-fiscal-data-page.vue'),
    beforeEnter: guardStep,
  },
  {
    path: '/thank-you/',
    name: 'thank-you',
    component: () => import('@/spa/contact-form/pages/thank-you-page.vue'),
    beforeEnter: guardStep,
  },
  {
    path: '*',
    redirect: { name: 'home' },
  },
];

//contingency solution, not to reuse, is not the right way to do stuff
const cfComponent = document.getElementsByTagName('contact-form-cmp');
let views = [],
  contentData = {};
if (cfComponent) {
  try {
    views = JSON.parse(cfComponent[0].getAttribute('data-views'));
    contentData = JSON.parse(cfComponent[0].getAttribute('data-content-data'));
    routes.forEach((r) => {
      const v = views.find((v) => v.name === r.name);
      if (v) {
        r.path = `/${v.slug}/`;
      }
    });
    debug &&
      colorLog({
        label: 'ROUTER',
        message: `ROUTES SLUG UPDATED CORRECTLY BEFORE ROUTER INIT`,
        themeName: 'vue',
      });
  } catch (e) {
    debug &&
      colorLog({
        label: 'CANNOT UPDATE ROUTER SLUGS BEFORE ROUTER INIT',
        message: `${e}`,
        themeName: 'red',
      });
  }
}

const router = new VueRouter({
  base: window.location.pathname,
  // mode: 'history',
  routes,
  scrollBehavior(to, from, savedPosition) {
    let y = 0;
    try {
      const heroCmp = router.app.$children.find(
        (e) => e.$options.name === 'HeroCmp'
      );
      y =
        heroCmp.$el.getBoundingClientRect().y +
        heroCmp.$el.getBoundingClientRect().height +
        document.documentElement.scrollTop;
    } catch (e) {
      // eslint-disable-next-line
      console.warn('cannot retrieve hero y');
    }
    return {
      x: 0,
      y,
      behavior: 'smooth',
    }; //
  },
});

const initApp = async (to, from, next) => {
  try {
    if (router.app.$store.state.appReady) {
      // App is ready, API already called
      next();
    } else {
      // App not ready, call API
      // Here you can call some api of initialization
      // Here you can set some data to store
      // Set appReady to true
      try {
        router.app.$store.commit('SET_PROPERTIES', {
          module: 'contact',
          property: 'views',
          value: [...views],
        });
        debug &&
          colorLog({
            label: 'ROUTER',
            message: `Views injected in store before router afterEach correctly`,
            themeName: 'vue',
          });
      } catch (e) {
        debug &&
          colorLog({
            label: 'ROUTER',
            message: `Could not inject views in store`,
            themeName: 'red',
          });
      }

      if (Object.keys(contentData).length) {
        try {
          router.app.$store.commit('SET_PROPERTIES', {
            module: 'contact',
            property: 'contentData',
            value: { ...contentData },
          });
          debug &&
            colorLog({
              label: 'ROUTER',
              message: `ContentData injected in store before router afterEach correctly`,
              themeName: 'vue',
            });
        } catch (e) {
          debug &&
            colorLog({
              label: 'ROUTER',
              message: `Could not inject contentData into views in store`,
              themeName: 'red',
            });
        }
      }

      await router.app.$store.dispatch('setAppReady', true);

      next();
    }
  } catch (e) {
    debug &&
      colorLog({
        label: 'Router error',
        message: `${e}`,
        themeName: 'red',
      });
    // Set appReady to true
    await router.app.$store.dispatch('setAppReady', true);

    // API error, redirect to homepage
    next({ name: 'home' });
  }
};

//To change in future projects because router hooks cannot be async
//they should alter app proper functionality
router.beforeEach(async (to, from, next) => {
  // Controllo bootstrapper
  if (!router.app.$store.state.vueBootstrapper.ready) {
    // Bootstrapper non completo mi metto in watch
    router.app.$store.watch(
      () => router.app.$store.state.vueBootstrapper.ready,
      (status) => {
        if (status) {
          // Bootstrapper completo, procedo
          initApp(to, from, next);
        }
      }
    );
  } else {
    // Bootstrapper completo, procedo
    initApp(to, from, next);
  }
});

router.afterEach((to, from) => {
  const newStep = router.app.$store.state.contact.views.find(
    (e) => to.name === e.name
  );
  if (newStep) {
    router.app.$store.dispatch('contact/setCurrentStep', newStep);
  }
});

// This implementation is deprecated because it launches in an async way the logic
// it leads to an issue about the order of the hooks of the router are not respected
// so we have unwanted behaviours
// const changeCurrentStep = (to) => {
//   const newStep = router.app.$store.state.contact.views.find(
//     (e) => to.name === e.slug
//   );
//   if (newStep) {
//     router.app.$store.dispatch('contact/setCurrentStep', newStep);
//   }
// };

// router.afterEach((to, from) => {
//   if (from) {
//     if (!router.app.$store.state.contact.ready) {
//       router.app.$store.watch(
//         () => router.app.$store.state.contact.ready,
//         (status) => {
//           if (status) {
//             changeCurrentStep(to);
//           }
//         }
//       );
//     } else {
//       changeCurrentStep(to);
//     }
//   }
// });

export default router;
